import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getUserConfigurationsLite } from '@services/configurationManager';
import type {
  GetResearchConfigurationsLiteParams,
  ResearchConfigurationLiteItem,
} from '@types';

const SCAN_PAGE_SIZE = 25;

export type SessionPickerCatalogFilters = Pick<
  GetResearchConfigurationsLiteParams,
  'searchId' | 'searchPatient' | 'searchDate'
>;

export const sessionPickerCatalogQueryKey = (
  userId?: string,
  filters?: SessionPickerCatalogFilters,
  existingSessionIds?: number[]
) =>
  [
    'session-picker-catalog',
    userId,
    filters?.searchId ?? '',
    filters?.searchPatient ?? '',
    filters?.searchDate ?? '',
    [...(existingSessionIds ?? [])].sort((left, right) => left - right),
  ] as const;

export const scanSessionPickerCatalog = async (
  userId: string,
  filters: SessionPickerCatalogFilters,
  existingSessionIds: number[]
): Promise<ResearchConfigurationLiteItem[]> => {
  const excluded = new Set(existingSessionIds);
  const available: ResearchConfigurationLiteItem[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const response = await getUserConfigurationsLite(userId, {
      status: 'completed',
      page,
      size: SCAN_PAGE_SIZE,
      ...filters,
    });
    totalPages = response.totalPages;
    available.push(...response.items.filter(item => !excluded.has(item.id)));
    page += 1;
  }

  return available;
};

type UseSessionPickerCatalogParams = {
  userId?: string;
  filters: SessionPickerCatalogFilters;
  existingSessionIds: number[];
  page: number;
  pageSize: number;
  enabled: boolean;
};

export const useSessionPickerCatalog = ({
  userId,
  filters,
  existingSessionIds,
  page,
  pageSize,
  enabled,
}: UseSessionPickerCatalogParams) => {
  const {
    data: catalog = [] as ResearchConfigurationLiteItem[],
    isLoading,
    isFetching,
    error,
  } = useQuery<ResearchConfigurationLiteItem[]>({
    queryKey: sessionPickerCatalogQueryKey(userId, filters, existingSessionIds),
    queryFn: () =>
      scanSessionPickerCatalog(userId!, filters, existingSessionIds),
    enabled: enabled && Boolean(userId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const totalAvailable = catalog.length;
  const totalPages =
    totalAvailable === 0 ? 0 : Math.ceil(totalAvailable / pageSize);
  const safePage = totalPages === 0 ? 1 : Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return catalog.slice(start, start + pageSize);
  }, [catalog, pageSize, safePage]);

  const getSessionById = useMemo(
    () => (sessionId: number) =>
      catalog.find(
        (item: ResearchConfigurationLiteItem) => item.id === sessionId
      ),
    [catalog]
  );

  return {
    catalog,
    pageItems,
    totalAvailable,
    totalPages,
    safePage,
    getSessionById,
    isLoading,
    isFetching,
    error,
  };
};
